import type { ShipmentContract, ShipmentRequest } from '../types/marketplace';
import { resolveMexicoStateDisplayCode } from './mexicoStateDisplayCode';

export interface RouteSummary {
    origin: string;
    destination: string;
}

type PointWithAddress = {
    full_address: string | null;
    zip: string;
    city?: string | null;
    municipality?: string | null;
    state?: string | null;
    state_code?: string | null;
    postal_code?: string | null;
};

function normalizeText(value: string | null | undefined): string | null {
    const normalizedValue = value?.trim();
    return normalizedValue ? normalizedValue : null;
}

function formatCompactLocation(
    city: string | null,
    state: string | null,
    postalCode: string | null,
): string | null {
    const locationSegments = [city, state, postalCode].filter(
        (segment): segment is string => Boolean(segment)
    );

    return locationSegments.length > 0 ? locationSegments.join(', ') : null;
}

function parseCompactLocationFromFullAddress(fullAddress: string | null | undefined): {
    city: string | null;
    state: string | null;
    postalCode: string | null;
} {
    const normalizedAddress = normalizeText(fullAddress);
    if (!normalizedAddress) {
        return { city: null, state: null, postalCode: null };
    }

    const postalCodeMatch = normalizedAddress.match(/\b(\d{5})\b/);
    const postalCode = postalCodeMatch?.[1] ?? null;

    const rawSegments = normalizedAddress.split(',').map((segment) => segment.trim()).filter(Boolean);
    if (rawSegments.length === 0) {
        return { city: null, state: null, postalCode };
    }

    const lastSegmentIndex = rawSegments.length - 1;
    const lastSegment = rawSegments[lastSegmentIndex];
    const hasCountrySegment = lastSegment?.toLowerCase().includes('mex') ?? false;
    const hasTrailingPostalCode = Boolean(postalCode && lastSegment?.includes(postalCode));
    const stateIndex = hasCountrySegment || hasTrailingPostalCode
        ? lastSegmentIndex - 1
        : lastSegmentIndex;
    const state = stateIndex >= 0 ? normalizeText(rawSegments[stateIndex]) : null;

    let city: string | null = null;

    if (postalCode) {
        const citySegment = rawSegments.find((segment) => segment.includes(postalCode));
        if (citySegment) {
            city = normalizeText(citySegment.replace(postalCode, '').replace(/^[-\s]+|[-\s]+$/g, ''));
        }
    }

    if (!city) {
        const cityIndex = stateIndex - 1;
        city = cityIndex >= 0 ? normalizeText(rawSegments[cityIndex]) : null;
    }

    return { city, state, postalCode };
}

function resolvePointLabel(point: PointWithAddress | undefined, fallback: string | undefined): string | null {
    const fullAddress = point?.full_address?.trim();
    if (fullAddress) {
        return fullAddress;
    }

    const zip = point?.zip.trim() || fallback?.trim();
    return zip || null;
}

function resolveCompactPointLabel(
    point: PointWithAddress | undefined,
    fallbackZip: string | undefined,
    fallbackLabel: string,
): string {
    const parsedFromFullAddress = parseCompactLocationFromFullAddress(point?.full_address);
    const city = normalizeText(point?.city)
        ?? normalizeText(point?.municipality)
        ?? parsedFromFullAddress.city;
    const fallbackState = normalizeText(point?.state)
        ?? parsedFromFullAddress.state;
    const state = resolveMexicoStateDisplayCode(
        point?.state_code,
        point?.state,
        parsedFromFullAddress.state,
    ) ?? fallbackState;
    const postalCode = normalizeText(point?.postal_code)
        ?? normalizeText(point?.zip)
        ?? normalizeText(fallbackZip)
        ?? parsedFromFullAddress.postalCode;

    return formatCompactLocation(city, state, postalCode) ?? fallbackLabel;
}

function splitRouteLabel(routeLabel: string | undefined | null): RouteSummary | null {
    const normalizedRouteLabel = routeLabel?.trim();
    if (!normalizedRouteLabel) {
        return null;
    }

    const separators = [' -> ', ' → ', ' - ', '->', '→'];
    for (const separator of separators) {
        const separatorIndex = normalizedRouteLabel.indexOf(separator);
        if (separatorIndex <= 0) {
            continue;
        }

        const origin = normalizedRouteLabel.slice(0, separatorIndex).trim();
        const destination = normalizedRouteLabel.slice(separatorIndex + separator.length).trim();

        if (origin && destination) {
            return { origin, destination };
        }
    }

    return null;
}

export function resolveRequestRouteSummary(
    request: ShipmentRequest,
    fallbackOrigin = 'Origen pendiente',
    fallbackDestination = 'Destino pendiente'
): RouteSummary {
    const orderedPoints = request.points
        ? [...request.points].sort((a, b) => a.point_order - b.point_order)
        : [];
    const firstPoint = orderedPoints[0];
    const lastPoint = orderedPoints[orderedPoints.length - 1];
    const splitHomeRouteLabel = splitRouteLabel(request.home_summary?.route_label);

    const origin = resolvePointLabel(firstPoint, request.origin_zip)
        || splitHomeRouteLabel?.origin
        || fallbackOrigin;
    const destination = resolvePointLabel(lastPoint, request.destination_zip)
        || splitHomeRouteLabel?.destination
        || fallbackDestination;

    return { origin, destination };
}

export function resolveRequestCompactRouteSummary(
    request: ShipmentRequest,
    fallbackOrigin = 'Origen pendiente',
    fallbackDestination = 'Destino pendiente'
): RouteSummary {
    const orderedPoints = request.points
        ? [...request.points].sort((a, b) => a.point_order - b.point_order)
        : [];
    const firstPoint = orderedPoints[0];
    const lastPoint = orderedPoints[orderedPoints.length - 1];

    return {
        origin: resolveCompactPointLabel(firstPoint, request.origin_zip, fallbackOrigin),
        destination: resolveCompactPointLabel(lastPoint, request.destination_zip, fallbackDestination),
    };
}

export function formatRouteDistanceKm(distanceMeters: number | null | undefined): string {
    if (typeof distanceMeters !== 'number' || !Number.isFinite(distanceMeters) || distanceMeters < 0) {
        return 'Sin estimación';
    }

    const distanceKm = distanceMeters / 1000;

    return `${distanceKm.toFixed(distanceKm >= 100 ? 0 : 1)} km`;
}

export function resolveContractRouteSummary(contract: ShipmentContract): RouteSummary {
    const orderedContractPoints = contract.points
        ? [...contract.points].sort((a, b) => a.point_order - b.point_order)
        : [];
    const orderedRequestPoints = contract.request?.points
        ? [...contract.request.points].sort((a, b) => a.point_order - b.point_order)
        : [];

    const firstContractPoint = orderedContractPoints[0];
    const lastContractPoint = orderedContractPoints[orderedContractPoints.length - 1];
    const firstRequestPoint = orderedRequestPoints[0];
    const lastRequestPoint = orderedRequestPoints[orderedRequestPoints.length - 1];
    const splitHomeRouteLabel = splitRouteLabel(contract.request?.home_summary?.route_label);

    const origin = resolvePointLabel(firstContractPoint, undefined)
        || resolvePointLabel(firstRequestPoint, contract.request?.origin_zip)
        || splitHomeRouteLabel?.origin
        || 'Origen pendiente';
    const destination = resolvePointLabel(lastContractPoint, undefined)
        || resolvePointLabel(lastRequestPoint, contract.request?.destination_zip)
        || splitHomeRouteLabel?.destination
        || 'Destino pendiente';

    return { origin, destination };
}

export function resolveContractCompactRouteSummary(contract: ShipmentContract): RouteSummary {
    const orderedContractPoints = contract.points
        ? [...contract.points].sort((a, b) => a.point_order - b.point_order)
        : [];
    const orderedRequestPoints = contract.request?.points
        ? [...contract.request.points].sort((a, b) => a.point_order - b.point_order)
        : [];

    const firstPoint = orderedContractPoints[0] ?? orderedRequestPoints[0];
    const lastPoint = orderedContractPoints[orderedContractPoints.length - 1]
        ?? orderedRequestPoints[orderedRequestPoints.length - 1];

    return {
        origin: resolveCompactPointLabel(firstPoint, contract.request?.origin_zip, 'Origen pendiente'),
        destination: resolveCompactPointLabel(lastPoint, contract.request?.destination_zip, 'Destino pendiente'),
    };
}
